package music.limasoul.client.shared.event.presentation.viewmodel

import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateOf
import music.jammers.client.shared.component.BaseViewModel
import music.jammers.client.shared.component.customExceptionHandler
import music.jammers.client.shared.component.update
import music.jammers.shared.event.domain.model.EventDetails
import music.jammers.shared.event.domain.model.EventFreezeData
import music.jammers.shared.event.track.domain.model.EventTrack
import music.limasoul.client.shared.event.domain.usecase.FreezeEventUseCase
import music.limasoul.client.shared.event.domain.usecase.GetEventDetailsUseCase
import music.limasoul.client.shared.event.domain.usecase.SortEventTrackUseCase
import music.limasoul.client.shared.navigation.Router

class EventFreezeViewModel(
    private val freezeEventUseCase: FreezeEventUseCase,
    private val getEventDetailsUseCase: GetEventDetailsUseCase,
    private val sortEventTracksUseCase: SortEventTrackUseCase,
    private val router: Router,
) : BaseViewModel() {

    internal val state: State<EventFreezeState> get() = _state
    private val _state = mutableStateOf(EventFreezeState(progress = true))

    internal fun onBind(eventId: Long) {
        _state.update {
            copy(
                eventId = eventId,
                progress = true,
            )
        }

        launch(
            customExceptionHandler(router) { _, throwable ->
                _state.update {
                    copy(
                        progress = false,
                        error = throwable
                    )
                }
            }
        ) {
            val eventDetails = getEventDetailsUseCase(eventId)
            updateTracks(eventDetails)
        }
    }

    private fun updateTracks(eventDetails: EventDetails) {
        val resultList: List<EventFreezeItem> = eventDetails.tracks.toEventItemList() +
            listOf(EventFreezeItem.BacklogHeader) +
            eventDetails.backlog.toEventItemList()

        _state.update {
            copy(
                progress = false,
                title = eventDetails.title,
                tracks = resultList,
                frozen = eventDetails.frozen,
            )
        }
    }

    private fun List<EventTrack>.toEventItemList(): List<EventFreezeItem> =
        map { value ->
            EventFreezeItem.DataItem(value)
        }

    private fun List<EventFreezeItem>.toEventTrackList(): List<EventTrack> =
        mapNotNull { value ->
            if (value is EventFreezeItem.DataItem) {
                value.eventTrack
            } else {
                null
            }
        }

    internal fun onErrorShown() {
        _state.update {
            copy(
                error = null,
            )
        }
    }

    internal fun onTrackListUpdated(eventTrackList: List<EventFreezeItem>) {
        _state.update {
            copy(
                tracks = eventTrackList
            )
        }
    }

    internal fun onBackPressed() {
        if (state.value.selectMode) {
            disableSelectMode()
        } else {
            router.back()
        }
    }

    private fun disableSelectMode() {
        _state.update {
            val updatedTracks = tracks.toMutableList().map { item ->
                if (item is EventFreezeItem.DataItem) {
                    item.copy(
                        checked = false,
                    )
                } else {
                    item
                }
            }
            copy(
                selectMode = false,
                tracks = updatedTracks
            )
        }
    }

    internal fun onFrozenChanged(value: Boolean) {
        _state.update {
            copy(
                frozen = value
            )
        }
    }

    internal fun onSaveClicked() {
        _state.update {
            copy(
                progress = true,
            )
        }

        val currentState = state.value
        val eventId = currentState.eventId

        launch(
            customExceptionHandler(router) { _, throwable ->
                _state.update {
                    copy(
                        progress = false,
                        error = throwable,
                    )
                }
            }
        ) {
            val currentTrackList = currentState.tracks
            val backlogHeaderIndex = currentTrackList.indexOf(EventFreezeItem.BacklogHeader)

            val trackList = currentTrackList.subList(0, backlogHeaderIndex).toEventTrackList()
            val backlogList = currentTrackList.subList(backlogHeaderIndex, currentTrackList.size).toEventTrackList()

            val freezeData = EventFreezeData(
                freeze = currentState.frozen,
                tracks = trackList,
                backlog = backlogList,
            )

            freezeEventUseCase.invoke(eventId, freezeData)
            router.back()
        }
    }

    internal fun onSelectModeClicked() {
        _state.update {
            copy(
                selectMode = true
            )
        }
    }

    internal fun onMoveToSetClicked() {
        _state.update {
            val selected = tracks.filter { item ->
                item is EventFreezeItem.DataItem && item.checked
            }

            val updatedTracks = tracks.toMutableList().apply {
                removeAll(selected)

                val backlogHeaderIndex: Int = indexOf(EventFreezeItem.BacklogHeader)

                addAll(backlogHeaderIndex, selected)
            }

            copy(
                tracks = updatedTracks,
            )
        }
        disableSelectMode()
    }

    internal fun onMoveToBacklogClicked() {
        _state.update {
            val selected = tracks.filter { item ->
                item is EventFreezeItem.DataItem && item.checked
            }

            val updatedTracks = tracks.toMutableList().apply {
                removeAll(selected)
                addAll(selected)
            }

            copy(
                tracks = updatedTracks,
            )
        }
        disableSelectMode()
    }

    internal fun onUpdateClicked() {
        _state.update {
            copy(
                progress = true,
            )
        }

        launch(
            customExceptionHandler(router) { _, throwable ->
                _state.update {
                    copy(
                        progress = false,
                        error = throwable,
                    )
                }
            }
        ) {
            val eventDetails = sortEventTracksUseCase.invoke(state.value.eventId)
            updateTracks(eventDetails)
        }
    }
}

internal data class EventFreezeState(
    val progress: Boolean,
    val eventId: Long = 0,
    val title: String = "",
    val tracks: List<EventFreezeItem> = emptyList(),
    val frozen: Boolean = false,
    val selectMode: Boolean = false,
    val error: Throwable? = null,
)

internal sealed class EventFreezeItem(
    val id: Long,
) {

    internal data class DataItem(
        val eventTrack: EventTrack,
        val checked: Boolean = false,
    ) : EventFreezeItem(eventTrack.id)

    internal data object BacklogHeader : EventFreezeItem(-1L)
}